import { useContext, useMemo, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { TranslationContext } from '../../../../assets/contexts/translationContext';
import { CrossIcon, RightArrowIcon } from '../../../../assets/icons/icons';
import { triggerImpactHaptic } from '../../../../assets/utils/hapticFeedback';
import BottomSheet from '../../../shared/BottomSheet/BottomSheet';
import CurrencyIcon from '../../../shared/CurrencyIcon/CurrencyIcon';
import './DepositOperations.css';

function formatTxHash(hash, edgeLength = 6) {
  const value = String(hash || '').trim();

  if (value.length <= edgeLength * 2 + 3) {
    return value;
  }

  return `${value.slice(0, edgeLength)}...${value.slice(-edgeLength)}`;
}

function openExternalLink(url) {
  if (!url) {
    return;
  }

  const webApp = window.Telegram?.WebApp;

  if (typeof webApp?.openLink === 'function') {
    webApp.openLink(url);
    return;
  }

  window.open(url, '_blank', 'noopener,noreferrer');
}

function DepositDetailRow({ label, value }) {
  return (
    <div className="deposit-operation-sheet__row">
      <span className="deposit-operation-sheet__label">{label}</span>
      <span className="deposit-operation-sheet__value">{value}</span>
    </div>
  );
}

function DepositOperationSheet({ operation, onClose, confirmationsText }) {
  const { common, deposit, history, t } = useContext(TranslationContext);
  const shouldReduceMotion = useReducedMotion();

  if (!operation) {
    return null;
  }

  const explorerLabel = operation.explorerName
    ? t('deposit.viewOnExplorer', { explorer: operation.explorerName })
    : deposit.viewOnBlockExplorer;

  return (
    <BottomSheet
      className="deposit-operation-sheet"
      panelClassName="deposit-operation-sheet__panel"
      titleId="deposit-operation-title"
      onClose={onClose}
    >
      <header className="deposit-operation-sheet__header">
        <h2 id="deposit-operation-title" className="deposit-operation-sheet__title">
          {history.transaction}
        </h2>
        <button
          type="button"
          className="deposit-operation-sheet__close"
          aria-label={common.close}
          onClick={onClose}
        >
          <CrossIcon className="deposit-operation-sheet__close-icon" />
        </button>
      </header>

      <div className="deposit-operation-sheet__hero">
        <CurrencyIcon
          className={[
            'deposit-operation-sheet__currency-icon',
            operation.confirmed && 'deposit-operation-sheet__currency-icon--confirmed',
          ].filter(Boolean).join(' ')}
          imageClassName="deposit-operation-sheet__currency-icon-image"
          currency={operation.raw?.currency}
          src={operation.currencyIconUrl}
          code={operation.asset}
        />

        <p
          className={
            operation.confirmed
              ? 'deposit-operation-sheet__amount deposit-operation-sheet__amount--confirmed'
              : 'deposit-operation-sheet__amount'
          }
        >
          {operation.amountAssetLabel}
        </p>
        <p className="deposit-operation-sheet__subtitle">{operation.amountUsdLabel}</p>
        <span
          className={
            operation.confirmed
              ? 'deposit-operation-sheet__status deposit-operation-sheet__status--confirmed'
              : 'deposit-operation-sheet__status'
          }
        >
          {operation.confirmed ? deposit.confirmed : deposit.confirmingOnChain}
        </span>
      </div>

      {!operation.confirmed ? (
        <div className="deposit-operation-sheet__confirmations-card">
          <div className="deposit-operation-sheet__confirmations-header">
            <span>{deposit.networkConfirmations}</span>
            <span>
              {operation.confirmations} / {operation.totalConfirmations}
            </span>
          </div>
          <div className="deposit-operation-sheet__confirmations-card-track" aria-hidden="true">
            <motion.span
              className="deposit-operation-sheet__confirmations-card-value"
              initial={false}
              animate={{ transform: `scaleX(${operation.progressPercent / 100})` }}
              transition={shouldReduceMotion
                ? { duration: 0 }
                : { duration: 0.32, ease: 'easeOut' }}
            />
          </div>
          <p className="deposit-operation-sheet__confirmations-text">
            {confirmationsText || deposit.transferDetected}
          </p>
        </div>
      ) : null}

      <div className="deposit-operation-sheet__card">
        <DepositDetailRow label={deposit.amount} value={operation.amountUsdLabel} />
        <DepositDetailRow label={deposit.depositAmount} value={operation.amountAssetLabel} />
        <DepositDetailRow label={deposit.network} value={operation.network} />
        {operation.txHash ? (
          <DepositDetailRow
            label={history.txHash}
            value={
              operation.explorerUrl ? (
                <button
                  type="button"
                  className="deposit-operation-sheet__link"
                  onClick={() => openExternalLink(operation.explorerUrl)}
                >
                  {formatTxHash(operation.txHash, 8)}
                </button>
              ) : (
                formatTxHash(operation.txHash, 8)
              )
            }
          />
        ) : null}
      </div>

      {operation.explorerUrl ? (
        <button
          type="button"
          className="deposit-operation-sheet__explorer"
          onClick={() => openExternalLink(operation.explorerUrl)}
        >
          <span>{explorerLabel}</span>
          <RightArrowIcon className="deposit-operation-sheet__explorer-icon" />
        </button>
      ) : null}
    </BottomSheet>
  );
}

function DepositOperations({
  operations = [],
  note,
  emptyText,
  className,
  showEmpty = true,
  showCardProgress = true,
  confirmationsText,
}) {
  const { deposit } = useContext(TranslationContext);
  const shouldReduceMotion = useReducedMotion();
  const [selectedOperationKey, setSelectedOperationKey] = useState(null);
  const selectedOperation = useMemo(
    () => operations.find((operation) => operation.key === selectedOperationKey) || null,
    [operations, selectedOperationKey],
  );
  const rootClassName = ['deposit-operations', className].filter(Boolean).join(' ');

  const handleOperationClick = (operation) => {
    triggerImpactHaptic('light');
    setSelectedOperationKey(operation.key);
  };

  return (
    <>
      <section className={rootClassName} aria-label={deposit.transactions}>
        <div className="deposit-operations__header">
          <h3 className="deposit-operations__title">{deposit.transactions}</h3>
          <span className="deposit-operations__count">{operations.length}</span>
        </div>

        {operations.length ? (
          <div className="deposit-operations__list">
            {operations.map((operation) => (
              <button
                key={operation.key}
                type="button"
                className={
                  showCardProgress
                    ? 'deposit-operations__operation'
                    : 'deposit-operations__operation deposit-operations__operation--compact'
                }
                onClick={() => handleOperationClick(operation)}
              >
                <div className="deposit-operations__operation-header">
                  <CurrencyIcon
                    className="deposit-operations__currency-icon"
                    imageClassName="deposit-operations__currency-icon-image"
                    currency={operation.raw?.currency}
                    src={operation.currencyIconUrl}
                    code={operation.asset}
                  />
                  <span className="deposit-operations__operation-copy">
                    <span className="deposit-operations__operation-amount">
                      {operation.amountAssetLabel}
                    </span>
                    <span className="deposit-operations__operation-meta">
                      {operation.amountUsdLabel} · {operation.network}
                    </span>
                  </span>
                </div>

                {showCardProgress ? (
                  <div className="deposit-operations__operation-progress-track" aria-hidden="true">
                    <motion.span
                      className="deposit-operations__operation-progress-fill"
                      initial={false}
                      animate={{ transform: `scaleX(${operation.progressPercent / 100})` }}
                      transition={shouldReduceMotion
                        ? { duration: 0 }
                        : { duration: 0.32, ease: 'easeOut' }}
                    />
                  </div>
                ) : null}

                <span className="deposit-operations__operation-side">
                  <span
                    className={
                      operation.confirmed
                        ? 'deposit-operations__operation-status deposit-operations__operation-status--confirmed'
                        : 'deposit-operations__operation-status'
                    }
                  >
                    {operation.confirmed
                      ? deposit.confirmed
                      : `${operation.confirmations}/${operation.totalConfirmations}`}
                  </span>
                  <RightArrowIcon className="deposit-operations__operation-chevron" />
                </span>
              </button>
            ))}
          </div>
        ) : showEmpty ? (
          <p className="deposit-operations__empty">
            {emptyText || deposit.noTransactionsYet}
          </p>
        ) : null}

        {note ? (
          <p className="deposit-operations__note">{note}</p>
        ) : null}
      </section>

      <AnimatePresence>
        {selectedOperation && (
          <DepositOperationSheet
            operation={selectedOperation}
            confirmationsText={confirmationsText}
            onClose={() => setSelectedOperationKey(null)}
          />
        )}
      </AnimatePresence>
    </>
  );
}

export default DepositOperations;
